import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  Users,
  Activity,
  Zap,
  Gauge,
  AlertTriangle,
  Clock,
  ChevronRight,
  ArrowLeft,
  MonitorPlay,
  Grid3X3,
  CalendarClock,
  Lightbulb,
  Info,
  AlertCircle,
  X,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { analyticsApi } from '@/api/client';
import type {
  AnalyticsOverview,
  SessionSummary,
  QualityBucket,
  RetentionPoint,
  HeatmapCell,
  PostingEngagement,
  SessionTimelinePoint,
  Insight,
  InsightCategory,
  InsightSeverity,
} from '@/types';

// ---- Helpers ----

function formatPlayhead(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type TabId = 'overview' | 'heatmap' | 'posting' | 'insights';

// ---- Stat Card ----

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color?: string;
  subtitle?: string;
}

function StatCard({ label, value, icon: Icon, color = 'text-gold', subtitle }: StatCardProps) {
  return (
    <div className="bg-surface border border-surface-elevated rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-silver-muted">{label}</span>
        <Icon size={20} className={color} />
      </div>
      <div className="text-2xl font-bold text-silver-bright">{value}</div>
      {subtitle && <div className="text-xs text-silver-muted mt-1">{subtitle}</div>}
    </div>
  );
}

// ---- Quality Timeline ----

function QualityTimeline({ data }: { data: QualityBucket[] }) {
  if (data.length === 0) {
    return <p className="text-sm text-silver-muted">No quality data available yet.</p>;
  }

  const maxTtfb = Math.max(...data.map(d => d.avgTtfb), 1);
  const maxBitrate = Math.max(...data.map(d => d.avgBitrate), 1);

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-xs text-silver-muted mb-2">Avg TTFB (ms)</h4>
        <div className="flex items-end gap-px h-16">
          {data.map((d, i) => (
            <div
              key={i}
              className="flex-1 bg-gold/70 rounded-t-sm hover:bg-gold transition-colors"
              style={{ height: `${Math.max(2, (d.avgTtfb / maxTtfb) * 100)}%` }}
              title={`${new Date(d.time).toLocaleTimeString()}: ${d.avgTtfb.toFixed(0)} ms`}
            />
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-xs text-silver-muted mb-2">Avg Bitrate (kbps)</h4>
        <div className="flex items-end gap-px h-16">
          {data.map((d, i) => (
            <div
              key={i}
              className="flex-1 bg-status-healthy/70 rounded-t-sm hover:bg-status-healthy transition-colors"
              style={{ height: `${Math.max(2, (d.avgBitrate / maxBitrate) * 100)}%` }}
              title={`${new Date(d.time).toLocaleTimeString()}: ${d.avgBitrate.toFixed(0)} kbps`}
            />
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-xs text-silver-muted mb-2">Buffer Starvation %</h4>
        <div className="flex items-end gap-px h-16">
          {data.map((d, i) => (
            <div
              key={i}
              className={cn(
                'flex-1 rounded-t-sm transition-colors',
                d.starvationPct > 0.1 ? 'bg-status-error/70 hover:bg-status-error' : 'bg-status-error/20 hover:bg-status-error/40'
              )}
              style={{ height: `${Math.max(2, d.starvationPct * 100)}%` }}
              title={`${new Date(d.time).toLocaleTimeString()}: ${(d.starvationPct * 100).toFixed(1)}%`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- Retention Chart ----

function RetentionChart({ data }: { data: RetentionPoint[] }) {
  if (data.length === 0) {
    return <p className="text-sm text-silver-muted">No retention data available.</p>;
  }

  const width = 600;
  const height = 140;
  const pad = { top: 10, right: 10, bottom: 24, left: 40 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const maxPt = Math.max(...data.map(d => d.playheadTime), 1);

  const points = data.map(d => ({
    x: pad.left + (d.playheadTime / maxPt) * plotW,
    y: pad.top + plotH - (d.retentionPct / 100) * plotH,
  }));

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const area = `${line} L${points[points.length - 1].x},${pad.top + plotH} L${points[0].x},${pad.top + plotH} Z`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      {/* Y axis labels */}
      {[0, 50, 100].map(v => {
        const y = pad.top + plotH - (v / 100) * plotH;
        return (
          <g key={v}>
            <line x1={pad.left} y1={y} x2={width - pad.right} y2={y} stroke="currentColor" className="text-surface-elevated" strokeWidth={0.5} />
            <text x={pad.left - 4} y={y + 3} textAnchor="end" className="fill-silver-muted" fontSize={9}>{v}%</text>
          </g>
        );
      })}
      {/* X axis labels */}
      {data.filter((_, i) => i % Math.max(1, Math.floor(data.length / 5)) === 0 || i === data.length - 1).map((d, i) => (
        <text
          key={i}
          x={pad.left + (d.playheadTime / maxPt) * plotW}
          y={height - 4}
          textAnchor="middle"
          className="fill-silver-muted"
          fontSize={9}
        >
          {formatPlayhead(d.playheadTime)}
        </text>
      ))}
      {/* Area + Line */}
      <path d={area} className="fill-gold/15" />
      <path d={line} fill="none" stroke="currentColor" className="text-gold" strokeWidth={2} />
      {/* Hover dots */}
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={3}
          className="fill-gold opacity-0 hover:opacity-100 transition-opacity"
        >
          <title>{`${formatPlayhead(data[i].playheadTime)}: ${data[i].retentionPct.toFixed(1)}% (${data[i].viewerCount} viewers)`}</title>
        </circle>
      ))}
    </svg>
  );
}

// ---- Watch Heatmap ----

function WatchHeatmap({ data }: { data: HeatmapCell[] }) {
  const grid = useMemo(() => {
    const cells: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    let max = 0;
    for (const d of data) {
      cells[d.dayOfWeek][d.hourOfDay] = d.sessionCount;
      if (d.sessionCount > max) max = d.sessionCount;
    }
    return { cells, max };
  }, [data]);

  const opacityStep = (count: number) => {
    if (grid.max === 0 || count === 0) return 0;
    const ratio = count / grid.max;
    if (ratio < 0.2) return 0.15;
    if (ratio < 0.4) return 0.3;
    if (ratio < 0.6) return 0.5;
    if (ratio < 0.8) return 0.7;
    return 1;
  };

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        {/* Hour labels */}
        <div className="flex ml-10 mb-1">
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="flex-1 text-center text-[10px] text-silver-muted">{h}</div>
          ))}
        </div>
        {/* Grid rows */}
        {DAY_LABELS.map((day, dow) => (
          <div key={dow} className="flex items-center gap-1 mb-px">
            <div className="w-9 text-right text-xs text-silver-muted pr-1">{day}</div>
            <div className="flex flex-1 gap-px">
              {Array.from({ length: 24 }, (_, h) => {
                const count = grid.cells[dow][h];
                return (
                  <div
                    key={h}
                    className="flex-1 aspect-square rounded-sm bg-gold transition-opacity"
                    style={{ opacity: opacityStep(count) || 0.04 }}
                    title={`${day} ${h}:00 — ${count} sessions`}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {/* Legend */}
      <div className="flex items-center gap-2 justify-end text-[10px] text-silver-muted">
        <span>Less</span>
        {[0.04, 0.15, 0.3, 0.5, 0.7, 1].map((o, i) => (
          <div key={i} className="w-3 h-3 rounded-sm bg-gold" style={{ opacity: o }} />
        ))}
        <span>More</span>
      </div>
    </div>
  );
}

// ---- Posting Analysis ----

function PostingAnalysis({ data }: { data: PostingEngagement[] }) {
  const hourlyAvg = useMemo(() => {
    const buckets: { total: number; count: number }[] = Array.from({ length: 24 }, () => ({ total: 0, count: 0 }));
    for (const d of data) {
      buckets[d.postingHour].total += d.viewerCount;
      buckets[d.postingHour].count += 1;
    }
    return buckets.map(b => (b.count > 0 ? b.total / b.count : 0));
  }, [data]);

  const maxAvg = Math.max(...hourlyAvg, 1);

  if (data.length === 0) {
    return <p className="text-sm text-silver-muted">No posting data available. Schedule entries will appear once the analytics-service receives channel.schedule events.</p>;
  }

  return (
    <div className="space-y-4">
      <h4 className="text-xs text-silver-muted">Avg Viewers by Content Posting Hour</h4>
      <div className="flex items-end gap-px h-32">
        {hourlyAvg.map((avg, h) => (
          <div
            key={h}
            className="flex-1 bg-gold/70 rounded-t-sm hover:bg-gold transition-colors relative group"
            style={{ height: `${Math.max(2, (avg / maxAvg) * 100)}%` }}
            title={`${h}:00 — avg ${avg.toFixed(1)} viewers`}
          />
        ))}
      </div>
      <div className="flex text-[10px] text-silver-muted">
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="flex-1 text-center">{h % 6 === 0 ? `${h}h` : ''}</div>
        ))}
      </div>
    </div>
  );
}

// ---- Insight Helpers ----

const SEVERITY_CONFIG: Record<InsightSeverity, { icon: React.ElementType; color: string; bg: string }> = {
  info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  warning: { icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  action: { icon: AlertCircle, color: 'text-status-error', bg: 'bg-status-error/10' },
};

const CATEGORY_COLORS: Record<InsightCategory, string> = {
  drop_off: 'bg-red-500/20 text-red-400',
  optimal_time: 'bg-green-500/20 text-green-400',
  quality_alert: 'bg-yellow-500/20 text-yellow-400',
  audience_trend: 'bg-blue-500/20 text-blue-400',
  peak_pattern: 'bg-purple-500/20 text-purple-400',
  engagement: 'bg-gold/20 text-gold',
};

const CATEGORY_LABELS: Record<InsightCategory, string> = {
  drop_off: 'Drop-off',
  optimal_time: 'Optimal Time',
  quality_alert: 'Quality',
  audience_trend: 'Trend',
  peak_pattern: 'Peak',
  engagement: 'Engagement',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function InsightCard({
  insight,
  onDismiss,
  onChannelClick,
}: {
  insight: Insight;
  onDismiss: (id: number) => void;
  onChannelClick: (channelId: string) => void;
}) {
  const sev = SEVERITY_CONFIG[insight.severity];
  const SevIcon = sev.icon;

  return (
    <div className={cn('border border-surface-elevated rounded-xl p-4', sev.bg)}>
      <div className="flex items-start gap-3">
        <SevIcon size={20} className={cn('mt-0.5 flex-shrink-0', sev.color)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={cn('text-[10px] font-medium px-2 py-0.5 rounded-full', CATEGORY_COLORS[insight.category])}>
              {CATEGORY_LABELS[insight.category]}
            </span>
            {insight.channelId && (
              <button
                onClick={() => onChannelClick(insight.channelId!)}
                className="text-[10px] font-mono text-gold hover:text-gold/80 transition-colors"
              >
                {insight.channelId.slice(0, 8)}...
              </button>
            )}
            <span className="text-[10px] text-silver-muted ml-auto flex-shrink-0">
              {timeAgo(insight.computedAt)}
            </span>
          </div>
          <h4 className="text-sm font-semibold text-silver-bright">{insight.title}</h4>
          <p className="text-sm text-silver-muted mt-1">{insight.description}</p>
          {insight.metricValue != null && insight.metricLabel && (
            <div className="mt-2">
              <span className="text-lg font-bold text-silver-bright">
                {Number.isInteger(insight.metricValue) ? insight.metricValue : insight.metricValue.toFixed(1)}
              </span>
              <span className="text-xs text-silver-muted ml-1.5">{insight.metricLabel}</span>
            </div>
          )}
        </div>
        <button
          onClick={() => onDismiss(insight.id)}
          className="text-silver-muted hover:text-silver-bright transition-colors flex-shrink-0"
          title="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

// ---- Session Detail ----

const STATE_COLORS: Record<string, string> = {
  p: 'bg-status-healthy',
  a: 'bg-gold',
  r: 'bg-status-error',
  k: 'bg-gold/50',
};

const STATE_LABELS: Record<string, string> = {
  p: 'Playing',
  a: 'Ad',
  r: 'Rebuffering',
  k: 'Seeking',
};

function SessionDetail({ sessionId, onBack }: { sessionId: string; onBack: () => void }) {
  const { data: timeline, isLoading } = useQuery<SessionTimelinePoint[]>({
    queryKey: ['analytics-session-timeline', sessionId],
    queryFn: () => analyticsApi.getSessionTimeline(sessionId),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gold" />
      </div>
    );
  }

  if (!timeline || timeline.length === 0) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="flex items-center gap-2 text-silver-muted hover:text-silver-bright transition-colors">
          <ArrowLeft size={16} />
          <span className="text-sm">Back</span>
        </button>
        <p className="text-sm text-silver-muted">No timeline data for this session.</p>
      </div>
    );
  }

  const w = 600;
  const h = 80;
  const padL = 60;
  const padR = 10;
  const plotW = w - padL - padR;

  const startT = new Date(timeline[0].time).getTime();
  const endT = new Date(timeline[timeline.length - 1].time).getTime();
  const dur = Math.max(endT - startT, 1);
  const xOf = (t: string) => padL + ((new Date(t).getTime() - startT) / dur) * plotW;

  // Playhead
  const playheadPts = timeline.filter(p => p.playheadTime != null);
  const maxPt = Math.max(...playheadPts.map(p => p.playheadTime!), 1);
  const playheadLine = playheadPts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xOf(p.time)},${8 + (h - 16) - (p.playheadTime! / maxPt) * (h - 16)}`)
    .join(' ');

  // Bitrate (step chart)
  const bitratePts = timeline.filter(p => p.bitrate != null);
  const maxBr = Math.max(...bitratePts.map(p => p.bitrate!), 1);
  const bitrateSteps = bitratePts
    .map((p, i) => {
      const x = xOf(p.time);
      const y = 8 + (h - 16) - (p.bitrate! / maxBr) * (h - 16);
      if (i === 0) return `M${x},${y}`;
      const prevX = xOf(bitratePts[i - 1].time);
      return `H${x} V${y}`;
    })
    .join(' ');

  // Buffer length (area)
  const bufferPts = timeline.filter(p => p.bufferLength != null);
  const maxBuf = Math.max(...bufferPts.map(p => p.bufferLength!), 1);
  const baseline = 8 + (h - 16);
  const bufferLine = bufferPts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xOf(p.time)},${8 + (h - 16) - (p.bufferLength! / maxBuf) * (h - 16)}`)
    .join(' ');
  const bufferArea = bufferPts.length > 0
    ? `${bufferLine} L${xOf(bufferPts[bufferPts.length - 1].time)},${baseline} L${xOf(bufferPts[0].time)},${baseline} Z`
    : '';
  const starvationPts = timeline.filter(p => p.bufferStarvation);

  // Player states
  const stateSegments: { x: number; w: number; state: string }[] = [];
  for (let i = 0; i < timeline.length; i++) {
    const s = timeline[i].playerState;
    if (!s) continue;
    const x1 = xOf(timeline[i].time);
    const x2 = i < timeline.length - 1 ? xOf(timeline[i + 1].time) : x1 + 2;
    stateSegments.push({ x: x1, w: Math.max(x2 - x1, 1), state: s });
  }

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-2 text-silver-muted hover:text-silver-bright transition-colors">
        <ArrowLeft size={16} />
        <span className="text-sm">Back</span>
      </button>
      <h3 className="text-sm font-semibold text-silver-bright">Session: <span className="font-mono">{sessionId.slice(0, 12)}...</span></h3>

      {/* Playhead Progression */}
      <div className="bg-surface border border-surface-elevated rounded-xl p-4">
        <h4 className="text-xs text-silver-muted mb-2">Playhead Time</h4>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="xMidYMid meet">
          <text x={padL - 4} y={14} textAnchor="end" className="fill-silver-muted" fontSize={9}>{formatPlayhead(maxPt)}</text>
          <text x={padL - 4} y={h - 4} textAnchor="end" className="fill-silver-muted" fontSize={9}>0:00</text>
          {playheadLine && <path d={playheadLine} fill="none" className="text-gold" stroke="currentColor" strokeWidth={1.5} />}
        </svg>
      </div>

      {/* Bitrate Switches */}
      <div className="bg-surface border border-surface-elevated rounded-xl p-4">
        <h4 className="text-xs text-silver-muted mb-2">Bitrate (kbps)</h4>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="xMidYMid meet">
          <text x={padL - 4} y={14} textAnchor="end" className="fill-silver-muted" fontSize={9}>{maxBr}</text>
          <text x={padL - 4} y={h - 4} textAnchor="end" className="fill-silver-muted" fontSize={9}>0</text>
          {bitrateSteps && <path d={bitrateSteps} fill="none" className="text-status-healthy" stroke="currentColor" strokeWidth={1.5} />}
        </svg>
      </div>

      {/* Buffer Length + Starvations */}
      <div className="bg-surface border border-surface-elevated rounded-xl p-4">
        <h4 className="text-xs text-silver-muted mb-2">Buffer Length (ms) + Starvation Events</h4>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="xMidYMid meet">
          <text x={padL - 4} y={14} textAnchor="end" className="fill-silver-muted" fontSize={9}>{maxBuf}</text>
          <text x={padL - 4} y={h - 4} textAnchor="end" className="fill-silver-muted" fontSize={9}>0</text>
          {bufferArea && <path d={bufferArea} className="fill-gold/15" />}
          {bufferLine && <path d={bufferLine} fill="none" className="text-gold" stroke="currentColor" strokeWidth={1} />}
          {starvationPts.map((p, i) => (
            <circle key={i} cx={xOf(p.time)} cy={baseline - 4} r={3} className="fill-status-error">
              <title>Buffer starvation at {new Date(p.time).toLocaleTimeString()}</title>
            </circle>
          ))}
        </svg>
      </div>

      {/* Player State Bar */}
      <div className="bg-surface border border-surface-elevated rounded-xl p-4">
        <h4 className="text-xs text-silver-muted mb-2">Player State</h4>
        <div className="h-6 flex rounded overflow-hidden bg-surface-deep">
          {stateSegments.map((seg, i) => (
            <div
              key={i}
              className={cn('h-full', STATE_COLORS[seg.state] ?? 'bg-silver-muted/30')}
              style={{ width: `${(seg.w / plotW) * 100}%` }}
              title={STATE_LABELS[seg.state] ?? seg.state}
            />
          ))}
        </div>
        <div className="flex gap-4 mt-2">
          {Object.entries(STATE_LABELS).map(([k, label]) => (
            <div key={k} className="flex items-center gap-1 text-[10px] text-silver-muted">
              <div className={cn('w-2.5 h-2.5 rounded-sm', STATE_COLORS[k])} />
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- Main Component ----

export default function Analytics() {
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Overview data
  const { data: overview, isLoading: overviewLoading, error: overviewError } = useQuery<AnalyticsOverview>({
    queryKey: ['analytics-overview'],
    queryFn: analyticsApi.getOverview,
    refetchInterval: 30_000,
  });

  // Sessions list
  const { data: sessions } = useQuery<SessionSummary[]>({
    queryKey: ['analytics-sessions', selectedChannel],
    queryFn: () => analyticsApi.getSessions(selectedChannel ?? undefined),
    refetchInterval: 30_000,
  });

  // Quality data for selected channel
  const { data: quality } = useQuery<QualityBucket[]>({
    queryKey: ['analytics-quality', selectedChannel],
    queryFn: () => analyticsApi.getQuality(selectedChannel!),
    enabled: !!selectedChannel,
    refetchInterval: 30_000,
  });

  // Retention curve for selected channel
  const { data: retention } = useQuery<RetentionPoint[]>({
    queryKey: ['analytics-retention', selectedChannel],
    queryFn: () => analyticsApi.getRetention(selectedChannel!),
    enabled: !!selectedChannel,
  });

  // Heatmap data
  const { data: heatmap, isLoading: heatmapLoading } = useQuery<HeatmapCell[]>({
    queryKey: ['analytics-heatmap'],
    queryFn: () => analyticsApi.getHeatmap(),
    enabled: activeTab === 'heatmap' && !selectedChannel && !selectedSession,
  });

  // Posting engagement
  const { data: postingData, isLoading: postingLoading } = useQuery<PostingEngagement[]>({
    queryKey: ['analytics-posting'],
    queryFn: () => analyticsApi.getPostingEngagement(),
    enabled: activeTab === 'posting' && !selectedChannel && !selectedSession,
  });

  // Insights
  const queryClient = useQueryClient();
  const { data: insights, isLoading: insightsLoading } = useQuery<Insight[]>({
    queryKey: ['analytics-insights'],
    queryFn: () => analyticsApi.getInsights(),
    enabled: activeTab === 'insights' && !selectedChannel && !selectedSession,
  });

  const refreshMutation = useMutation({
    mutationFn: analyticsApi.refreshInsights,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['analytics-insights'] }),
  });

  const dismissMutation = useMutation({
    mutationFn: analyticsApi.dismissInsight,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['analytics-insights'] }),
  });

  const handleDismiss = useCallback((id: number) => {
    dismissMutation.mutate(id);
  }, [dismissMutation]);

  if (overviewLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold" />
      </div>
    );
  }

  // ---- Session Detail View ----
  if (selectedSession) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-silver-bright">Session Detail</h2>
            <p className="text-sm text-silver-muted mt-1">CMCD timeline drill-down</p>
          </div>
          <BarChart3 size={24} className="text-gold" />
        </div>
        <SessionDetail
          sessionId={selectedSession}
          onBack={() => setSelectedSession(null)}
        />
      </div>
    );
  }

  // ---- Channel Detail View ----
  if (selectedChannel) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => setSelectedChannel(null)}
              className="flex items-center gap-2 text-silver-muted hover:text-silver-bright transition-colors mb-1"
            >
              <ArrowLeft size={16} />
              <span className="text-sm">Back to overview</span>
            </button>
            <h2 className="text-2xl font-bold text-silver-bright">
              Channel: {selectedChannel.slice(0, 8)}...
            </h2>
            <p className="text-sm text-silver-muted mt-1">QoE metrics and session detail</p>
          </div>
          <BarChart3 size={24} className="text-gold" />
        </div>

        {/* Retention Curve */}
        {retention && retention.length > 0 && (
          <div className="bg-surface border border-surface-elevated rounded-xl p-5">
            <h3 className="text-sm font-semibold text-silver-bright mb-4">Viewer Retention</h3>
            <RetentionChart data={retention} />
          </div>
        )}

        {/* Quality Timeline */}
        {quality && (
          <div className="bg-surface border border-surface-elevated rounded-xl p-5">
            <h3 className="text-sm font-semibold text-silver-bright mb-4">QoE Time Series (1-min buckets)</h3>
            <QualityTimeline data={quality} />
          </div>
        )}

        {/* Sessions Table (clickable for drill-down) */}
        <div className="bg-surface border border-surface-elevated rounded-xl p-5">
          <h3 className="text-sm font-semibold text-silver-bright mb-4">Channel Sessions</h3>
          {sessions && sessions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-silver-muted border-b border-surface-elevated">
                    <th className="pb-2 font-medium">Session</th>
                    <th className="pb-2 font-medium text-right">Reports</th>
                    <th className="pb-2 font-medium text-right">Avg TTFB</th>
                    <th className="pb-2 font-medium text-right">Avg Bitrate</th>
                    <th className="pb-2 font-medium text-right">Starvations</th>
                    <th className="pb-2 font-medium text-right">Dropped</th>
                    <th className="pb-2 font-medium text-right">MSD</th>
                    <th className="pb-2 font-medium text-right">Started</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr
                      key={s.sessionId}
                      onClick={() => setSelectedSession(s.sessionId)}
                      className="border-b border-surface-elevated/50 hover:bg-surface-elevated/30 cursor-pointer transition-colors"
                    >
                      <td className="py-2.5 font-mono text-xs text-silver-bright">
                        <div className="flex items-center gap-2">
                          <MonitorPlay size={14} className={s.endedAt ? 'text-silver-muted' : 'text-status-healthy'} />
                          {s.sessionId.slice(0, 8)}...
                        </div>
                      </td>
                      <td className="py-2.5 text-right text-silver-bright">{s.reportCount}</td>
                      <td className="py-2.5 text-right text-silver-bright">
                        {s.avgTtfb > 0 ? `${s.avgTtfb.toFixed(0)} ms` : '-'}
                      </td>
                      <td className="py-2.5 text-right text-silver-bright">
                        {s.avgBitrate > 0 ? `${s.avgBitrate.toFixed(0)}` : '-'}
                      </td>
                      <td className="py-2.5 text-right">
                        <span className={s.bufferStarvations > 0 ? 'text-status-error' : 'text-silver-muted'}>
                          {s.bufferStarvations}
                        </span>
                      </td>
                      <td className="py-2.5 text-right">
                        <span className={s.droppedFrames > 10 ? 'text-status-warning' : 'text-silver-muted'}>
                          {s.droppedFrames}
                        </span>
                      </td>
                      <td className="py-2.5 text-right text-silver-muted">
                        {s.mediaStartDelay != null ? `${s.mediaStartDelay} ms` : '-'}
                      </td>
                      <td className="py-2.5 text-right text-silver-muted text-xs">
                        <div className="flex items-center justify-end gap-1">
                          <Clock size={12} />
                          {new Date(s.startedAt).toLocaleTimeString()}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-silver-muted">No sessions recorded for this channel.</p>
          )}
        </div>
      </div>
    );
  }

  // ---- Overview / Tabs View ----
  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'heatmap', label: 'Watch Heatmap', icon: Grid3X3 },
    { id: 'posting', label: 'Posting Analysis', icon: CalendarClock },
    { id: 'insights', label: 'Insights', icon: Lightbulb },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-silver-bright">Analytics</h2>
          <p className="text-sm text-silver-muted mt-1">CMCD v2 viewer telemetry — last 24h</p>
        </div>
        <BarChart3 size={24} className="text-gold" />
      </div>

      {/* Tab Strip */}
      <div className="flex gap-1 bg-surface-deep rounded-lg p-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'bg-surface text-silver-bright shadow-sm'
                : 'text-silver-muted hover:text-silver-bright'
            )}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {!overview && !overviewLoading && (
            <div className="bg-surface border border-surface-elevated rounded-xl p-8 text-center">
              <BarChart3 size={32} className="mx-auto text-silver-muted/30 mb-3" />
              <p className="text-sm text-silver-muted">
                {overviewError ? 'Failed to load analytics data. The analytics-service may be unavailable.' : 'No analytics data available. Start streaming to generate telemetry.'}
              </p>
            </div>
          )}
          {overview && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <StatCard label="Total Sessions" value={overview.totalSessions} icon={Users} />
              <StatCard label="Active Now" value={overview.activeSessions} icon={Activity} color="text-status-healthy" />
              <StatCard label="Avg TTFB" value={`${overview.avgTtfb.toFixed(0)} ms`} icon={Zap} color="text-gold" />
              <StatCard label="Avg Bitrate" value={`${overview.avgBitrate.toFixed(0)} kbps`} icon={Gauge} color="text-status-healthy" />
              <StatCard
                label="Buffer Starvation"
                value={`${(overview.bufferStarvationRate * 100).toFixed(1)}%`}
                icon={AlertTriangle}
                color={overview.bufferStarvationRate > 0.05 ? 'text-status-error' : 'text-silver-muted'}
              />
            </div>
          )}

          {/* Top Channels Table */}
          {overview && overview.topChannels.length > 0 && (
            <div className="bg-surface border border-surface-elevated rounded-xl p-5">
              <h3 className="text-sm font-semibold text-silver-bright mb-4">Top Channels</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-silver-muted border-b border-surface-elevated">
                    <th className="pb-2 font-medium">Channel</th>
                    <th className="pb-2 font-medium text-right">Viewers</th>
                    <th className="pb-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {overview.topChannels.map((ch) => (
                    <tr
                      key={ch.channelId}
                      onClick={() => setSelectedChannel(ch.channelId)}
                      className="border-b border-surface-elevated/50 hover:bg-surface-elevated/30 cursor-pointer transition-colors"
                    >
                      <td className="py-3 text-silver-bright font-mono text-xs">{ch.channelName}</td>
                      <td className="py-3 text-right text-silver-bright">{ch.viewerCount}</td>
                      <td className="py-3 text-right"><ChevronRight size={14} className="text-silver-muted" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Sessions Table */}
          <div className="bg-surface border border-surface-elevated rounded-xl p-5">
            <h3 className="text-sm font-semibold text-silver-bright mb-4">Recent Sessions</h3>
            {sessions && sessions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-silver-muted border-b border-surface-elevated">
                      <th className="pb-2 font-medium">Session</th>
                      <th className="pb-2 font-medium">Channel</th>
                      <th className="pb-2 font-medium text-right">Reports</th>
                      <th className="pb-2 font-medium text-right">Avg TTFB</th>
                      <th className="pb-2 font-medium text-right">Avg Bitrate</th>
                      <th className="pb-2 font-medium text-right">Starvations</th>
                      <th className="pb-2 font-medium text-right">Dropped</th>
                      <th className="pb-2 font-medium text-right">MSD</th>
                      <th className="pb-2 font-medium text-right">Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s) => (
                      <tr
                        key={s.sessionId}
                        onClick={() => setSelectedSession(s.sessionId)}
                        className="border-b border-surface-elevated/50 hover:bg-surface-elevated/30 cursor-pointer transition-colors"
                      >
                        <td className="py-2.5 font-mono text-xs text-silver-bright">
                          <div className="flex items-center gap-2">
                            <MonitorPlay size={14} className={s.endedAt ? 'text-silver-muted' : 'text-status-healthy'} />
                            {s.sessionId.slice(0, 8)}...
                          </div>
                        </td>
                        <td className="py-2.5 font-mono text-xs text-silver-muted">
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedChannel(s.channelId); }}
                            className="hover:text-gold transition-colors"
                          >
                            {s.channelId.slice(0, 8)}...
                          </button>
                        </td>
                        <td className="py-2.5 text-right text-silver-bright">{s.reportCount}</td>
                        <td className="py-2.5 text-right text-silver-bright">
                          {s.avgTtfb > 0 ? `${s.avgTtfb.toFixed(0)} ms` : '-'}
                        </td>
                        <td className="py-2.5 text-right text-silver-bright">
                          {s.avgBitrate > 0 ? `${s.avgBitrate.toFixed(0)}` : '-'}
                        </td>
                        <td className="py-2.5 text-right">
                          <span className={s.bufferStarvations > 0 ? 'text-status-error' : 'text-silver-muted'}>
                            {s.bufferStarvations}
                          </span>
                        </td>
                        <td className="py-2.5 text-right">
                          <span className={s.droppedFrames > 10 ? 'text-status-warning' : 'text-silver-muted'}>
                            {s.droppedFrames}
                          </span>
                        </td>
                        <td className="py-2.5 text-right text-silver-muted">
                          {s.mediaStartDelay != null ? `${s.mediaStartDelay} ms` : '-'}
                        </td>
                        <td className="py-2.5 text-right text-silver-muted text-xs">
                          <div className="flex items-center justify-end gap-1">
                            <Clock size={12} />
                            {new Date(s.startedAt).toLocaleTimeString()}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-silver-muted">No sessions recorded yet. Play a channel to generate telemetry.</p>
            )}
          </div>
        </>
      )}

      {/* Heatmap Tab */}
      {activeTab === 'heatmap' && (
        <div className="bg-surface border border-surface-elevated rounded-xl p-5">
          <h3 className="text-sm font-semibold text-silver-bright mb-4">Watch Activity Heatmap (last 7 days)</h3>
          {heatmapLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gold" />
            </div>
          ) : heatmap && heatmap.length > 0 ? (
            <WatchHeatmap data={heatmap} />
          ) : (
            <p className="text-sm text-silver-muted">No watch activity data available. Session data will appear once viewers start streaming.</p>
          )}
        </div>
      )}

      {/* Posting Tab */}
      {activeTab === 'posting' && (
        <div className="bg-surface border border-surface-elevated rounded-xl p-5">
          <h3 className="text-sm font-semibold text-silver-bright mb-4">Posting Time Analysis (last 30 days)</h3>
          {postingLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gold" />
            </div>
          ) : postingData ? (
            <PostingAnalysis data={postingData} />
          ) : (
            <p className="text-sm text-silver-muted">No posting data available. Schedule entries will appear once the analytics-service receives channel.schedule events.</p>
          )}
        </div>
      )}

      {/* Insights Tab */}
      {activeTab === 'insights' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-silver-bright">Analytics Intelligence</h3>
            <button
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                'bg-surface border border-surface-elevated hover:bg-surface-elevated',
                'text-silver-muted hover:text-silver-bright',
                refreshMutation.isPending && 'opacity-50 cursor-not-allowed'
              )}
            >
              <RefreshCw size={14} className={refreshMutation.isPending ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {insightsLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gold" />
            </div>
          ) : insights && insights.length > 0 ? (
            <div className="space-y-3">
              {insights.map((insight) => (
                <InsightCard
                  key={insight.id}
                  insight={insight}
                  onDismiss={handleDismiss}
                  onChannelClick={setSelectedChannel}
                />
              ))}
            </div>
          ) : (
            <div className="bg-surface border border-surface-elevated rounded-xl p-8 text-center">
              <Lightbulb size={32} className="mx-auto text-silver-muted/30 mb-3" />
              <p className="text-sm text-silver-muted">
                No insights yet. Insights are computed hourly from viewer telemetry.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
